import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, TrendingUp } from 'lucide-react';
import PropertyCard from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useProjects } from '../hooks/useProjects';
import { useTranslation } from 'react-i18next';

const Properties = () => {
  const { t, i18n } = useTranslation();
  const { properties, loading } = useProjects();
  const activeRegions = [...new Set(properties.map((p) => p.location))];
  const [searchParams] = useSearchParams();
  const regionFromUrl = searchParams.get('region');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState(regionFromUrl || 'all');
  const [selectedType, setSelectedType] = useState('all');

  // Update filter when URL parameter changes
  useEffect(() => {
    if (regionFromUrl) {
      setSelectedRegion(regionFromUrl);
    }
  }, [regionFromUrl]);

  // Set page title
  useEffect(() => {
    if (selectedRegion && selectedRegion !== 'all') {
      document.title = `Investment Cases in ${selectedRegion} | Fundora Global`;
    } else {
      document.title = 'Investment Cases | Fundora Global';
    }
  }, [selectedRegion]);

  const filteredProperties = properties.filter((property) => {
    const matchesSearch = property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          property.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = selectedRegion === 'all' || property.location === selectedRegion;
    
    // Include "Mixed" type in both Apartment and Villa filters
    let matchesType = selectedType === 'all';
    if (!matchesType) {
      if (selectedType === 'Apartment') {
        matchesType = property.type === 'Apartment' || property.type === 'Mixed';
      } else if (selectedType === 'Villa') {
        matchesType = property.type === 'Villa' || property.type === 'Mixed';
      } else {
        matchesType = property.type === selectedType;
      }
    }
    
    return matchesSearch && matchesRegion && matchesType;
  });

  // Get translated investment info for the selected region
  const getRegionInvestmentInfo = (region) => {
    const regionKeyMap = {
      'Northern Cyprus': 'northernCyprus',
      'Bali (Indonesia)': 'bali',
      'Vietnam': 'vietnam',
      'Maldives': 'maldives'
    };
    const key = regionKeyMap[region];
    if (key) {
      // Use accusative case for Russian "Why invest in..." phrase
      const nameAccusative = t(`regions.${key}.nameAccusative`, { defaultValue: '' });
      return {
        name: t(`regions.${key}.name`),
        nameAccusative: nameAccusative || t(`regions.${key}.name`),
        investmentInfo: t(`regions.${key}.investmentInfo`)
      };
    }
    return null;
  };

  const selectedRegionInfo = getRegionInvestmentInfo(selectedRegion);

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#102131] to-[#1a3449] text-[#F7FDFF] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">
            {t('cases.title')}
          </h1>
          <p className="text-xl text-[#F7FDFF]/80 max-w-2xl">
            {t('cases.subtitle')}
          </p>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="bg-white border-b shadow-sm sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder={t('cases.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-gray-300 focus:border-[#CDA259]"
              />
            </div>

            {/* Region Filter */}
            <Select value={selectedRegion} onValueChange={setSelectedRegion}>
              <SelectTrigger className="w-full md:w-48 border-gray-300">
                <SlidersHorizontal className="mr-2" size={16} />
                <SelectValue placeholder={t('cases.allRegions')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('cases.allRegions')}</SelectItem>
                {[
                  { value: 'Northern Cyprus', key: 'northernCyprus' },
                  { value: 'Bali (Indonesia)', key: 'bali' },
                  { value: 'Vietnam', key: 'vietnam' },
                  { value: 'Maldives', key: 'maldives' }
                ]
                  .filter((region) => activeRegions.includes(region.value))
                  .map((region) => (
                    <SelectItem key={region.key} value={region.value}>{t(`regions.${region.key}.name`)}</SelectItem>
                  ))}
              </SelectContent>
            </Select>

            {/* Type Filter */}
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-full md:w-48 border-gray-300">
                <SelectValue placeholder={t('cases.allTypes')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('cases.allTypes')}</SelectItem>
                <SelectItem value="Villa">{t('cases.villa')}</SelectItem>
                <SelectItem value="Apartment">{t('cases.apartment')}</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              className="border-[#CDA259] text-[#102131] hover:bg-[#CDA259]/10"
              onClick={() => {
                setSearchTerm('');
                setSelectedRegion('all');
                setSelectedType('all');
              }}
            >
              {t('cases.clearFilters')}
            </Button>
          </div>
        </div>
      </section>

      {/* Properties Grid */}
      <section className="py-12 bg-[#F7FDFF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Investment Information Banner */}
          {selectedRegion !== 'all' && selectedRegionInfo && selectedRegionInfo.investmentInfo && (
            <div className="mb-8 p-6 bg-white rounded-xl shadow-md border-l-4 border-[#CDA259]">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#CDA259]/10 rounded-full flex items-center justify-center">
                    <TrendingUp className="text-[#CDA259]" size={24} />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#102131] mb-2">
                    {t('cases.whyInvest')} {selectedRegionInfo.nameAccusative}?
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {selectedRegionInfo.investmentInfo}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="mb-6">
            <p className="text-gray-600">
              {t('cases.showing')} <span className="font-semibold text-[#CDA259]">{filteredProperties.length}</span> {t('cases.casesCount')}
            </p>
          </div>

          {filteredProperties.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProperties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          ) : loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="cases-loading">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-96 rounded-xl bg-white/70 animate-pulse border border-gray-100" />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600">{t('cases.noResults')}</p>
              <Button
                className="mt-6 bg-[#CDA259] text-[#102131] hover:bg-[#CDA259]/90"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedRegion('all');
                  setSelectedType('all');
                }}
              >
                {t('cases.clearAllFilters')}
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Properties;
